import React, { useState, useRef } from 'react'
import styled from 'styled-components'
import { parseHtmlToOperations, operationsToCsv } from 'utils/paymentsFile'
import UploadFileOutlinedIcon from '@mui/icons-material/UploadFileOutlined'
import { toast } from 'react-hot-toast'

const downloadCsv = (content, filename) => {
  const blob = new Blob(['\ufeff' + content], { type: 'text/csv;charset=utf-8' })
  const url = URL.createObjectURL(blob)
  const a = document.createElement('a')
  a.href = url
  a.download = filename
  a.click()
  URL.revokeObjectURL(url)
}

const PreparePaymentsFileCard = () => {
  const fileInputRef = useRef(null)
  const [fileName, setFileName] = useState('')
  const [isDragging, setIsDragging] = useState(false)
  const [isProcessing, setIsProcessing] = useState(false)

  const processFile = file => {
    setIsProcessing(true)
    const reader = new FileReader()
    reader.onload = ev => {
      const html = ev.target.result
      const { operations, total } = parseHtmlToOperations(html)
      if (operations.length === 0) {
        toast.error('Nie znaleziono operacji do wyeksportowania')
      } else {
        const csv = operationsToCsv(operations, total)
        const baseName = file.name.replace(/\.html?$/i, '')
        downloadCsv(csv, `${baseName}_wpłaty.csv`)
        toast.success(`Pobrano plik z ${operations.length} operacjami`)
      }
      setIsProcessing(false)
    }
    reader.readAsText(file, 'UTF-8')
    setFileName(file.name)
  }

  const handleFileChange = e => {
    const file = e.target.files?.[0]
    if (!file) return
    processFile(file)
    if (fileInputRef.current) fileInputRef.current.value = ''
  }

  const handleDrop = e => {
    e.preventDefault()
    setIsDragging(false)
    const file = e.dataTransfer?.files?.[0]
    if (!file || !file.name.toLowerCase().endsWith('.html')) return
    processFile(file)
  }

  const handleZoneClick = () => fileInputRef.current?.click()

  return (
    <Card>
      <CardTitle>Przygotuj plik do wpłat</CardTitle>
      <CardDescription>
        Wgraj plik HTML z zestawieniem operacji bankowych. Aplikacja wyciągnie dane i pobierze plik CSV gotowy do importu wpłat.
      </CardDescription>
      <FileDropZone
        isDragging={isDragging}
        onClick={handleZoneClick}
        onDrop={handleDrop}
        onDragOver={e => { e.preventDefault(); setIsDragging(true) }}
        onDragLeave={() => setIsDragging(false)}
      >
        <HiddenInput
          ref={fileInputRef}
          type='file'
          accept='.html,text/html'
          onChange={handleFileChange}
        />
        <UploadIcon />
        <ZoneText>
          {fileName
            ? `Wybrano: ${fileName}`
            : 'Przeciągnij plik HTML tutaj lub kliknij, aby wybrać'}
        </ZoneText>
        <ZoneHint>Obsługiwany format: HTML</ZoneHint>
      </FileDropZone>
      {isProcessing && <ProcessingText>Przetwarzanie...</ProcessingText>}
    </Card>
  )
}

export default PreparePaymentsFileCard

const Card = styled.div`
  background: #fff;
  border-radius: 8px;
  padding: 24px;
  border: 1px solid ${({ theme }) => theme.colors.border || '#e0e0e0'};
  max-width: 500px;
`

const CardTitle = styled.div`
  font-size: 18px;
  font-weight: 600;
  margin-bottom: 8px;
  color: ${({ theme }) => theme.colors.text || '#333'};
`

const CardDescription = styled.div`
  font-size: 13px;
  color: ${({ theme }) => theme.colors.darkGrey || '#666'};
  margin-bottom: 20px;
`

const FileDropZone = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  gap: 12px;
  padding: 32px 24px;
  border: 2px dashed ${({ isDragging, theme }) =>
    isDragging ? theme.colors.primary.main : (theme.colors.border || '#ddd')};
  border-radius: 8px;
  background-color: ${({ isDragging, theme }) =>
    isDragging ? (theme.colors.primary?.light || '#e3f2fd') + '40' : (theme.colors.lightGrey || '#f5f5f5') + '60'};
  cursor: pointer;
  transition: all 0.2s ease;

  &:hover {
    border-color: ${({ theme }) => theme.colors.primary?.main || '#1976d2'};
    background-color: ${({ theme }) => (theme.colors.primary?.light || '#e3f2fd') + '30'};
  }
`

const HiddenInput = styled.input`
  display: none;
`

const UploadIcon = styled(UploadFileOutlinedIcon)`
  font-size: 48px !important;
  color: ${({ theme }) => theme.colors.secondary?.main || '#9c27b0'};
  opacity: 0.85;
`

const ZoneText = styled.span`
  font-size: 14px;
  color: ${({ theme }) => theme.colors.text || '#333'};
  text-align: center;
`

const ZoneHint = styled.span`
  font-size: 12px;
  color: ${({ theme }) => theme.colors.darkGrey || '#666'};
`

const ProcessingText = styled.div`
  margin-top: 12px;
  font-size: 13px;
  color: ${({ theme }) => theme.colors.darkGrey || '#666'};
`
